import type { ChangeEventHandler } from 'react'

import { FvLinkButton } from '@fv/client-components'
import type { SelectFieldOption } from '@fv/client-types'

import {
  InputAdornment,
  InputGroupWrapper,
} from '@/components/inputs/InputGroup'
import { SearchField } from '@/components/inputs/SearchField'
import { SelectField } from '@/components/inputs/SelectField'
import {
  getFilterQueryData,
  isSavedFilterGroup,
  type SavedFilter,
  type SavedFilterGroup,
} from '@/types/SavedFilter'

import { useFilterControls } from './hooks'
import { useShipmentFilterActions } from './ShipmentFilterListProvider'
import type { FilterControlProps } from './types'

export const CompactFilterBar = ({ onChange }: FilterControlProps) => {
  const {
    search,
    setSearch,
    filters,
    flattenedFilters,
    canSearch,
    activeFilter,
    defaultFilter,
    isCustomFilter,
  } = useFilterControls()
  const { setForcePanelOpen } = useShipmentFilterActions()
  const options = [
    ...buildOptionsFromFilters(filters),
    ...(isCustomFilter ? [{ text: 'Custom', value: 'custom' }] : []),
  ]

  const handleSelectChange: ChangeEventHandler<HTMLSelectElement> = e => {
    const selected = flattenedFilters.find(f => f._id === e.target.value)
    handleChange(selected)
  }

  const handleChange = (filter?: SavedFilter | SavedFilterGroup) => {
    onChange?.({
      ...(filter && getFilterQueryData(filter)),
    })
  }

  const handleEditClick = () => {
    setForcePanelOpen(true)
  }

  return (
    <div className="flex flex-1 items-center gap-2">
      <InputGroupWrapper name="filter" className="mb-0">
        <InputAdornment
          position="start"
          icon="filter"
          className="text-fv-gray-dark"
        />
        <SelectField
          className="form-control form-control--select w-auto"
          name="filter"
          onChange={handleSelectChange}
          options={options}
          value={
            isCustomFilter ? 'custom' : (activeFilter?._id ?? defaultFilter._id)
          }
        />
      </InputGroupWrapper>
      {canSearch && (
        <SearchField
          initialValue={search}
          onChange={search => setSearch(search)}
          className="mb-0 flex-1"
        />
      )}
      <FvLinkButton onClick={handleEditClick} icon="pen-alt" className="ml-2">
        Edit filters
      </FvLinkButton>
    </div>
  )
}

const buildOptionsFromFilters = (
  filters: SavedFilter[] | SavedFilterGroup[],
): SelectFieldOption[] => {
  return filters.flatMap<SelectFieldOption>(f => [
    ...(f.isDisabled ? [] : [{ text: f.name, value: f._id }]),
    ...(isSavedFilterGroup(f)
      ? f.children.map(c => ({
          text: c.fullLabel ?? c.name ?? '',
          value: c._id,
        }))
      : []),
  ])
}
